import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import { api } from '../api';
import Icon from './Icon';

interface Celebration { pending: boolean; amount: number }

const CONFETTI_COLORS = ['#fbbf24', '#34d399', '#60a5fa', '#f472b6', '#a78bfa', '#f87171'];
// Deterministic confetti pieces (no Math.random at module load): spread across
// the width with varied colour, delay, and duration.
const CONFETTI = Array.from({ length: 36 }, (_, i) => ({
  left: (i * 2.7) % 100,
  color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  delay: (i % 12) * 0.18,
  duration: 2.6 + (i % 5) * 0.4,
  size: 6 + (i % 4) * 2,
}));

/**
 * Post-grant "congratulations" popup for the flat first-deposit bonus. Shows once
 * — the backend reports a pending celebration until the app acks it. Re-checks
 * when an order update lands so it fires right after the deposit completes.
 */
export default function FirstDepositCelebrationPopup() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { orderUpdateCount } = useSocket();
  const [amount, setAmount] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get<Celebration>('/promotions/first-deposit-bonus/celebration')
      .then(res => {
        if (cancelled) return;
        if (res.data?.pending) { setAmount(res.data.amount || 0); setVisible(true); }
      })
      .catch(() => { /* best-effort */ });
    return () => { cancelled = true; };
  }, [orderUpdateCount]);

  const close = () => {
    setVisible(false);
    api.post('/promotions/first-deposit-bonus/celebration/ack').catch(() => { /* best-effort */ });
  };

  if (!visible) return null;

  return (
    <div
      onClick={close}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        backgroundColor: 'rgba(2,6,23,0.78)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 28,
        overflow: 'hidden',
      }}
    >
      <style>{`@keyframes aga-confetti-fall { 0% { transform: translateY(-12vh) rotate(0deg); opacity: 0; } 10% { opacity: 1; } 100% { transform: translateY(112vh) rotate(540deg); opacity: 1; } }`}</style>
      {/* Confetti backdrop */}
      {CONFETTI.map((c, i) => (
        <span
          key={i}
          style={{
            position: 'absolute', top: 0, left: `${c.left}%`,
            width: c.size, height: c.size * 1.6, backgroundColor: c.color, borderRadius: 1,
            animation: `aga-confetti-fall ${c.duration}s linear ${c.delay}s infinite`,
            pointerEvents: 'none',
          }}
        />
      ))}

      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative', width: '100%', maxWidth: 380, borderRadius: 22, padding: 28,
          backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
          animation: 'aga-modal-in 220ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <div
          style={{
            width: 84, height: 84, borderRadius: 42, marginBottom: 18,
            backgroundColor: colors.accentSoft,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon name="gift" size={38} color={colors.accentText} />
        </div>
        <div style={{ fontSize: 22, fontWeight: 900, color: colors.text, marginBottom: 10 }}>
          {t('firstDepositBonus.celebrationTitle')}
        </div>
        <div style={{ fontSize: 15, lineHeight: 1.55, color: colors.textSub, marginBottom: 24 }}>
          {t('firstDepositBonus.celebrationBody', { amount: amount.toFixed(2) })}
        </div>
        <button
          onClick={close}
          style={{
            width: '100%', minHeight: 50, borderRadius: 999, border: 'none', cursor: 'pointer',
            backgroundColor: colors.accent, color: '#020617', fontSize: 15, fontWeight: 800,
          }}
        >
          {t('firstDepositBonus.celebrationConfirm')}
        </button>
      </div>
    </div>
  );
}
